import CloseIcon from '@mui/icons-material/Close'
import {
  Alert,
  AppBar,
  Box,
  BoxProps,
  Button,
  Dialog,
  DialogContent,
  Grid,
  GridProps,
  IconButton,
  Slide,
  Snackbar,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import {
  forwardRef,
  ReactElement,
  Ref,
  SyntheticEvent,
  useMemo,
  useState,
} from 'react' // Import MUI icons
import DriveEtaIcon from '@mui/icons-material/DriveEta' // Import MUI icons
import { TransitionProps } from '@mui/material/transitions' // Import MUI transition props definition
import { Form, Formik, FormikHelpers } from 'formik' // Import Formik
import { useTranslation } from 'react-i18next' // Import i18n translation hook
import { addVehiclePhoto, updateVehiclePhoto } from '../../utils/driver-profile'
import FileUpload from '../forms/FileUploadWrapper'
import SubmitButton from '../forms/SubmitButtonWrapper' // Import custom Submit button wrapper

import { SnackBarAlertType, VehiclePayloadType } from '../../types/interface' // Import form type definition
import { useGetVehiclePhotos } from '../../../hooks/api/vehicles/photos/useGetVehiclePhotos'
import { VehiclesPhotosByDriverData } from '../../../types/interfaces'

const Transition = forwardRef(
  (
    props: {
      children: ReactElement
    } & TransitionProps,
    ref: Ref<unknown>
  ) => <Slide direction="up" ref={ref} {...props} />
)

const VehiclePhotos = ({
  city,
  vehicleId,
}: {
  city?: string
  vehicleId: number
}) => {
  const { data, error, isLoading, refetch } = useGetVehiclePhotos(vehicleId)
  const { t } = useTranslation()
  const [open, setOpen] = useState<boolean>(false)
  const [alertEvent, setAlertEvent] = useState<SnackBarAlertType>({
    message: '',
    open: false,
    type: 'info',
  })
  const theme = useTheme()
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('md'))

  const vehiclePhotosData = data as Partial<VehiclesPhotosByDriverData>

  const configGridContainer: GridProps = {
    container: true,
    rowGap: 4,
  }

  const configGridItem: GridProps = {
    item: true,
    xs: 12,
  }

  const boxConfig: BoxProps = {
    sx: {
      backgroundColor: '#FFF',
      bottom: 0,
      display: 'flex',
      left: 0,
      p: 1,
      position: 'fixed',
      right: 0,
      zIndex: 1000,
      ...(isLargeScreen && { justifyContent: 'center' }),
    },
  }

  const submitConfig = {
    ...(isLargeScreen
      ? {
          sx: {
            width: '30%',
          },
        }
      : {
          fullWidth: true,
        }),
  }

  const validateVehiclePhoto = async (values: Partial<VehiclePayloadType>) => {
    const errors: Partial<Record<keyof VehiclePayloadType, string>> = {}
    const requiredFields: Array<keyof VehiclePayloadType> = [
      'back',
      'back_seats',
      'front',
      'front_seats',
      'left',
      'odometer',
      'right',
      'trunk',
    ]
    requiredFields.forEach((field) => {
      if (field in values && !values[field]) {
        errors[field] =
          `Please upload the ${field.replace('_', ' ')} of your vehicle`
      }
    })
    return errors
  }

  const isCompleteVehiclePayload = (
    values: Partial<VehiclePayloadType>
  ): boolean => {
    const requiredFields: Array<keyof VehiclePayloadType> = [
      'back',
      'back_seats',
      'front',
      'front_seats',
      'left',
      'odometer',
      'right',
      'trunk',
    ]
    return requiredFields.every(
      (field) => values[field] && values[field].trim() !== ''
    )
  }

  const handleSubmit = async (
    values: Omit<VehiclePayloadType, 'vehicle_id'>,
    { setSubmitting }: FormikHelpers<VehiclePayloadType>
  ) => {
    if (!vehicleId) {
      setAlertEvent({
        message: `${t('documents.vehicle_photos.alerts.error')}`,
        open: true,
        type: 'error',
      })

      setSubmitting(false)
      return
    }

    const isComplete = isCompleteVehiclePayload(values)
    if (isComplete) {
      await addVehiclePhoto({
        ...values,
        vehicle_id: vehicleId,
      })
    } else {
      await updateVehiclePhoto({
        ...values,
        vehicle_id: vehicleId,
      })
    }
    setAlertEvent({
      message: `${t('documents.vehicle_photos.alerts.success')}`,
      open: true,
      type: 'success',
    })
    setSubmitting(false)
    setOpen(false)
    refetch()
  }

  const handleClose = (event: Event | SyntheticEvent, reason?: string) => {
    // Close snackbar
    if (reason === 'clickaway') {
      return
    }
    setAlertEvent({ ...alertEvent, open: false })
  }

  // Checks if all values are true in an object
  const isEmpty = vehiclePhotosData?.items?.some((item) => item?.photo === null)

  const initialValues = useMemo(() => {
    if (!vehiclePhotosData?.items) {
      return {}
    }

    return vehiclePhotosData?.items?.reduce((map, obj) => {
      if (!obj.photo) {
        return {
          ...map,
          [obj.key]: null,
        }
      }
      return map
    }, {} as Partial<VehiclePayloadType>)
  }, [vehiclePhotosData?.items])

  if (isLoading) {
    return <> {t('general.loading')}... </>
  }

  if (error) {
    return <>Something went wrong</>
  }

  return (
    <>
      {isEmpty && (
        <Button
          fullWidth
          onClick={() => setOpen(true)}
          size="large"
          startIcon={<DriveEtaIcon />}
          variant="contained"
        >
          {t('documents.vehicle_photos.cta')}
        </Button>
      )}

      <Dialog fullScreen open={open} TransitionComponent={Transition}>
        <AppBar color="dense" sx={{ position: 'relative' }}>
          <Toolbar>
            <IconButton
              aria-label="close"
              color="inherit"
              edge="start"
              onClick={() => setOpen(false)}
            >
              <CloseIcon />
            </IconButton>
            <Typography component="div" sx={{ flexGrow: 1 }} variant="h6">
              {t('documents.vehicle_photos.title')}
            </Typography>
          </Toolbar>
        </AppBar>
        <DialogContent
          sx={{
            alignItems: 'flex-start',
            display: 'flex',
            justifyContent: 'center',
            pb: 10,
          }}
        >
          <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validate={validateVehiclePhoto}
          >
            <Form>
              <Grid {...configGridContainer}>
                <Grid {...configGridItem}>
                  {vehiclePhotosData?.items?.map(
                    (item) =>
                      !item.photo && (
                        <Grid
                          key={item.type_id}
                          {...configGridItem}
                          sx={{ py: 2 }}
                        >
                          <FileUpload
                            city={city}
                            documentType="picture"
                            folder={`Vehicle/${vehicleId}/Photos`}
                            label={t('documents.vehicle_photos.label', {
                              item,
                            })}
                            name={item.key}
                          />
                        </Grid>
                      )
                  )}
                </Grid>
              </Grid>
              <Box {...boxConfig}>
                <SubmitButton {...submitConfig}>
                  {t('documents.vehicle_photos.submit')}
                </SubmitButton>
              </Box>
            </Form>
          </Formik>
        </DialogContent>
      </Dialog>
      <Snackbar
        anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
        autoHideDuration={6000}
        onClose={handleClose}
        open={alertEvent.open}
      >
        <Alert
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              onClick={handleClose}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          }
          elevation={6}
          onClose={handleClose}
          severity={alertEvent.type}
          sx={{
            width: '100%',
          }}
        >
          {alertEvent.message}
        </Alert>
      </Snackbar>
    </>
  )
}

export default VehiclePhotos
